import * as XLSX from "xlsx";
import { extendedKeyLabelMap } from "../constants/column.constants";

// ---------- Shared Utils ----------
function formatKey(key: string): string {
  return (
    extendedKeyLabelMap[key] ||
    key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

function isPlainObject(v: any): v is Record<string, any> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function ensureXlsx(name: string): string {
  return name.toLowerCase().endsWith(".xlsx")
    ? name
    : name.replace(/\.[^.]+$/, "") + ".xlsx";
}

function ensureCsv(name: string): string {
  return name.toLowerCase().endsWith(".csv")
    ? name
    : name.replace(/\.[^.]+$/, "") + ".csv";
}

function downloadCSV(csvString: string, filename: string) {
  try {
    const bom = "\uFEFF";
    const blob = new Blob([bom + csvString], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = ensureCsv(filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (e) {
    console.error("CSV export failed:", e);
  }
}

function downloadJSON(data: any, filename: string) {
  try {
    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: "application/json" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  } catch (e) {
    console.error("JSON export failed:", e);
  }
}

// ---------- Flatten Helpers ----------
function flattenObject(
  obj: Record<string, any>,
  parentKey = "",
  skipKeys: string[] = [],
): Record<string, any> {
  const out: Record<string, any> = {};
  for (const k in obj) {
    if (skipKeys.includes(k)) continue;
    const v = obj[k];
    const newKey = parentKey ? `${parentKey} ${formatKey(k)}` : formatKey(k);

    if (isPlainObject(v)) {
      Object.assign(out, flattenObject(v, newKey, skipKeys));
    } else if (Array.isArray(v)) {
      out[newKey] = v.join(", ");
    } else {
      out[newKey] = v;
    }
  }
  return out;
}

function flattenFirstItem(arr: any[], prefix: string): Record<string, any> {
  const out: Record<string, any> = {};
  if (!Array.isArray(arr) || arr.length === 0) return out;
  Object.assign(out, flattenObject(arr[0], prefix));
  out[`${prefix} Count`] = arr.length;
  return out;
}

function flattenAndMapKeys(item: any): Record<string, any> {
  const flat: Record<string, any> = {};
  for (const key in item) {
    const value = item[key];
    const base = formatKey(key);

    if (isPlainObject(value)) {
      Object.assign(flat, flattenObject(value, base));
    } else if (Array.isArray(value)) {
      if (key === "media_source") {
        Object.assign(flat, flattenFirstItem(value, "Media Source"));
      } else if (key === "language") {
        Object.assign(flat, flattenFirstItem(value, "Language"));
      } else if (key === "influencer") {
        Object.assign(flat, flattenFirstItem(value, "Influencer"));
      } else {
        flat[base] = value.join(", ");
      }
    } else {
      flat[base] = value;
    }
  }
  return flat;
}

// ---------- Keyword Export ----------
const PLATFORM_NAMES: Record<string, string> = {
  facebook: "Facebook",
  twitter: "Twitter",
  instagram: "Instagram",
  youtube: "YouTube",
  reddit: "Reddit",
  tiktok: "TikTok",
};

export function exportKeywordResultsToXLSX(
  apiData: any,
  filename = "keyword-results.xlsx",
) {
  const data = apiData?.data ?? apiData?.[0]?.data ?? apiData;
  if (!data || typeof data !== "object") {
    console.error("Invalid keyword data");
    return;
  }

  const wb = XLSX.utils.book_new();
  let any = false;

  Object.keys(PLATFORM_NAMES).forEach((platform) => {
    if (Array.isArray(data[platform]) && data[platform].length > 0) {
      const rows = data[platform].map(flattenAndMapKeys);
      const ws = XLSX.utils.json_to_sheet(rows);
      XLSX.utils.book_append_sheet(wb, ws, PLATFORM_NAMES[platform]);
      any = true;
    }
  });

  if (!any) {
    const ws = XLSX.utils.json_to_sheet([{ Message: "No data available" }]);
    XLSX.utils.book_append_sheet(wb, ws, "No Data");
  }

  XLSX.writeFile(wb, ensureXlsx(filename));
}

export function exportKeywordResultsToCSV(
  apiData: any,
  filename = "keyword-results.csv",
) {
  const data = apiData?.data ?? apiData?.[0]?.data ?? apiData;
  if (!data || typeof data !== "object") {
    console.error("Invalid keyword data");
    return;
  }

  const rows: Record<string, any>[] = [];
  Object.keys(PLATFORM_NAMES).forEach((platform) => {
    if (Array.isArray(data[platform]) && data[platform].length > 0) {
      data[platform].forEach((item: any) => {
        rows.push({
          Platform: PLATFORM_NAMES[platform],
          ...flattenAndMapKeys(item),
        });
      });
    }
  });

  if (rows.length === 0) {
    rows.push({ Platform: "", Message: "No data available" });
  }

  const ws = XLSX.utils.json_to_sheet(rows);
  const csv = XLSX.utils.sheet_to_csv(ws);
  downloadCSV(csv, filename);
}

export function exportKeywordResultsToJSON(
  apiData: any,
  filename = "keyword-results.json",
) {
  const data = apiData?.data ?? apiData?.[0]?.data ?? apiData;
  downloadJSON(data, filename);
}

// ---------- Post Export ----------
export function exportPostResultToXLSX(
  apiData: any,
  filename = "post-result.xlsx",
) {
  const data = apiData?.data ?? apiData;
  if (!data || typeof data !== "object") {
    console.error("Invalid post data");
    return;
  }
  const ws = XLSX.utils.json_to_sheet([flattenAndMapKeys(data)]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Post");
  XLSX.writeFile(wb, ensureXlsx(filename));
}

export function exportPostResultToCSV(
  apiData: any,
  filename = "post-result.csv",
) {
  const data = apiData?.data ?? apiData;
  if (!data || typeof data !== "object") {
    console.error("Invalid post data");
    return;
  }
  const ws = XLSX.utils.json_to_sheet([flattenAndMapKeys(data)]);
  const csv = XLSX.utils.sheet_to_csv(ws);
  downloadCSV(csv, filename);
}

export function exportPostResultToJSON(
  apiData: any,
  filename = "post-result.json",
) {
  const data = apiData?.data ?? apiData;
  downloadJSON(data, filename);
}

// ---------- Media Export ----------
const MEDIA_TYPES = [
  { key: "print", label: "Print" },
  { key: "online_news", label: "Online News" },
  { key: "tv", label: "TV" },
  { key: "radio", label: "Radio" },
];

export function exportMediaResultsToXLSX(
  apiData: any,
  filename = "media-results.xlsx",
) {
  if (!apiData || typeof apiData !== "object") {
    console.error("Invalid media data");
    return;
  }

  const wb = XLSX.utils.book_new();
  let any = false;

  for (const { key, label } of MEDIA_TYPES) {
    const section = apiData[key];
    if (!section) continue;

    const rows: any[] = [];
    for (const publisher in section) {
      const articles = section[publisher];
      if (!Array.isArray(articles)) continue;
      articles.forEach((a) =>
        rows.push({ publisher, ...flattenAndMapKeys(a) }),
      );
    }

    if (rows.length > 0) {
      const ws = XLSX.utils.json_to_sheet(rows);
      XLSX.utils.book_append_sheet(wb, ws, label);
      any = true;
    }
  }

  if (!any) {
    const ws = XLSX.utils.json_to_sheet([{ Message: "No media data" }]);
    XLSX.utils.book_append_sheet(wb, ws, "No Data");
  }

  XLSX.writeFile(wb, ensureXlsx(filename));
}

export function exportMediaResultsToCSV(
  apiData: any,
  filename = "media-results.csv",
) {
  if (!apiData || typeof apiData !== "object") {
    console.error("Invalid media data");
    return;
  }

  const rows: Record<string, any>[] = [];
  for (const { key, label } of MEDIA_TYPES) {
    const section = apiData[key];
    if (!section) continue;
    for (const publisher in section) {
      const articles = section[publisher];
      if (!Array.isArray(articles)) continue;
      articles.forEach((a) =>
        rows.push({
          Type: label,
          publisher,
          ...flattenAndMapKeys(a),
        }),
      );
    }
  }

  if (rows.length === 0) {
    rows.push({ Type: "", Message: "No media data" });
  }

  const ws = XLSX.utils.json_to_sheet(rows);
  const csv = XLSX.utils.sheet_to_csv(ws);
  downloadCSV(csv, filename);
}

export function exportMediaResultsToJSON(
  apiData: any,
  filename = "media-results.json",
) {
  downloadJSON(apiData, filename);
}

export const exportInfluencerResultsToXLSX = (
  data: any[],
  fileName = "influencer_results.xlsx",
) => {
  if (!data || data.length === 0) return;

  // Flatten rows
  const flattened: any[] = [];

  data.forEach((row) => {
    row.data.forEach((poster: any) => {
      flattened.push({
        RowIndex: row.rowIndex,
        Links: row.links?.join(", ") || "",
        PosterID: poster._id,
        Source: poster.social_poster_source,
        Type: poster.social_poster_type,
        Username: poster.social_poster_username,
        ProfileURL: poster.social_poster_profile_url,
        Followers: poster.social_poster_follower_count,
        Following: poster.social_poster_following_count,
        Posts: poster.social_poster_content_count,
        Likes: poster.social_poster_likes_count,
        Verified: poster.social_poster_verified,
        Business: poster.social_poster_business,
        StartedDate: poster.social_poster_started_date,
        Country: poster.social_poster_country,
        Status: poster.social_poster_account_status,
      });
    });
  });

  // Convert to worksheet
  const ws = XLSX.utils.json_to_sheet(flattened);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Influencers");

  // Export file
  XLSX.writeFile(wb, fileName);
};

export const exportInfluencerResultsToCSV = (
  data: any[],
  fileName = "influencer_results.csv",
) => {
  if (!data || data.length === 0) return;

  const flattened: any[] = [];
  data.forEach((row) => {
    row.data.forEach((poster: any) => {
      flattened.push({
        RowIndex: row.rowIndex,
        Links: row.links?.join(", ") || "",
        PosterID: poster._id,
        Source: poster.social_poster_source,
        Type: poster.social_poster_type,
        Username: poster.social_poster_username,
        ProfileURL: poster.social_poster_profile_url,
        Followers: poster.social_poster_follower_count,
        Following: poster.social_poster_following_count,
        Posts: poster.social_poster_content_count,
        Likes: poster.social_poster_likes_count,
        Verified: poster.social_poster_verified,
        Business: poster.social_poster_business,
        StartedDate: poster.social_poster_started_date,
        Country: poster.social_poster_country,
        Status: poster.social_poster_account_status,
      });
    });
  });

  const ws = XLSX.utils.json_to_sheet(flattened);
  const csv = XLSX.utils.sheet_to_csv(ws);
  downloadCSV(csv, fileName);
};
